/**
 * Live alerts as a waterfall: standing conditions pinned, repeats grouped.
 *
 * Inspired by a tactical alert feed, drawn in Pathfinder's own tones — gold
 * for warnings, coral for something that is still true, quiet grey for a
 * clearance. No new typefaces; the dock already has a voice.
 */
import React, { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import ShieldIcon from '@mui/icons-material/GppGoodOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CampaignIcon from '@mui/icons-material/Campaign';
import HistoryIcon from '@mui/icons-material/History';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Mission,
  MissionRouteEvent,
  MissionRouteStatus,
  MissionTimelineStop,
  SentinelAction,
  acknowledgeRouteEvents,
  getMissionRouteEvents,
  promoteRouteEvent,
  trustIdentifier,
} from '../../services/missionApi';
import { AlertGroup, buildAlertFeed } from './alertWaterfall';
import { formatWhen } from './format';
import { Action, GOLD } from './missionLiveParts';
import DockStatStrip from './DockStatStrip';
import { DockTabHelp } from './DockTabHelp';
import { metalSx } from '../../theme/metalButtons';
import MissionThreatReport from './MissionThreatReport';
import { buildThreatReport, trackedKind } from './threatReportView';
import PromoteToPacketDialog, { PromoteTarget } from '../../components/intel/PromoteToPacketDialog';

const CORAL = '#ef5350';
const CORAL_SOFT = '#ef5350';
const AMBER = '#f0b429';
const MUTED = '#8a939f';
const DIM = '#5b6570';
const EDGE = '#262b33';

const TONE_COLOR: Record<AlertGroup['tone'], string> = {
  crit: CORAL_SOFT,
  warn: AMBER,
  ok: DIM,
};

const TONE_RAIL: Record<AlertGroup['tone'], string> = {
  crit: CORAL,
  warn: AMBER,
  ok: 'transparent',
};

export interface MissionAlertWaterfallProps {
  mission: Mission;
  events: MissionRouteEvent[];
  deviceLabel: (deviceId: string) => string;
  units?: 'km' | 'mi';
  checking?: boolean;
  emptyHint?: string;
  onFocus?: (longitude: number, latitude: number) => void;
  onAcked: (status: MissionRouteStatus) => void;
  onRouteAround?: (action: SentinelAction, group: AlertGroup) => void;
  routing?: boolean;
  timeline?: MissionTimelineStop[] | null;
}

function ago(iso: string): string {
  const text = formatWhen(iso);
  if (text === 'just now') return 'now';
  return text.replace(' ago', '');
}

const Entity: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="span"
    sx={{
      fontSize: '0.64rem',
      color: '#c8cfd7',
      bgcolor: 'rgba(255,255,255,0.05)',
      border: `1px solid ${EDGE}`,
      px: 0.75,
      borderRadius: 1.2,
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </Box>
);

const WaterfallRow: React.FC<{
  group: AlertGroup;
  onAck: () => void;
  onFocus?: () => void;
  onWatchlist?: () => void;
  onExcludeOwn?: () => void;
  watchlisting?: boolean;
  excluding?: boolean;
}> = ({ group, onAck, onFocus, onWatchlist, onExcludeOwn, watchlisting, excluding }) => {
  const [open, setOpen] = useState(false);
  const expandable = group.count > 1;

  return (
    <Box sx={{ borderBottom: `1px solid ${EDGE}` }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '3px 1fr auto auto auto auto',
          alignItems: 'center',
          gap: 1.25,
          py: 0.7,
          pr: 0.25,
          pl: 0,
          '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' },
          '&:hover .ack': { opacity: 1 },
        }}
      >
        <Box sx={{ alignSelf: 'stretch', borderRadius: '0 2px 2px 0', bgcolor: TONE_RAIL[group.tone] }} />
        <Box
          onClick={onFocus}
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 0.75,
            minWidth: 0,
            flexWrap: 'wrap',
            cursor: onFocus ? 'pointer' : 'default',
          }}
        >
          <Typography
            variant="caption"
            sx={{ fontWeight: group.tone === 'ok' ? 500 : 700, fontSize: '0.72rem', color: TONE_COLOR[group.tone] }}
          >
            {group.title}
          </Typography>
          {group.entity && <Entity>{group.entity}</Entity>}
          {expandable && (
            <Box
              component="span"
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((v) => !v); }
              }}
              sx={{
                fontSize: '0.6rem',
                fontWeight: 700,
                color: MUTED,
                bgcolor: 'rgba(255,255,255,0.05)',
                border: `1px solid ${EDGE}`,
                px: 0.65,
                borderRadius: 1.1,
                cursor: 'pointer',
                '&:hover': { color: 'text.primary' },
              }}
            >
              ×{group.count}
            </Box>
          )}
          {group.detail && !expandable && (
            <Typography variant="caption" sx={{ fontSize: '0.64rem', color: DIM }}>
              {group.detail}
            </Typography>
          )}
        </Box>
        {group.canExcludeOwn && (
          <Box
            component="span"
            role="button"
            tabIndex={excluding ? undefined : 0}
            title="This is ours — stop treating it as a follower"
            onClick={(e) => {
              e.stopPropagation();
              if (!excluding) onExcludeOwn?.();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !excluding) onExcludeOwn?.();
            }}
            sx={{
              display: 'flex',
              color: MUTED,
              cursor: excluding ? 'default' : 'pointer',
              '&:hover': excluding ? undefined : { color: GOLD },
            }}
          >
            <ShieldIcon sx={{ fontSize: 15 }} />
          </Box>
        )}
        {group.promoteEventId && (
          <Box
            component="span"
            role="button"
            tabIndex={group.alreadyWatchlisted || watchlisting ? undefined : 0}
            title={group.alreadyWatchlisted ? 'Already watchlisted' : 'Add to watchlist'}
            onClick={(e) => {
              e.stopPropagation();
              if (!group.alreadyWatchlisted && !watchlisting) onWatchlist?.();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !group.alreadyWatchlisted && !watchlisting) onWatchlist?.();
            }}
            sx={{
              display: 'flex',
              color: group.alreadyWatchlisted ? DIM : MUTED,
              cursor: group.alreadyWatchlisted || watchlisting ? 'default' : 'pointer',
              '&:hover': group.alreadyWatchlisted ? undefined : { color: GOLD },
            }}
          >
            <PlaylistAddIcon sx={{ fontSize: 15 }} />
          </Box>
        )}
        {!group.acknowledged && (
          <Typography
            className="ack"
            variant="caption"
            role="button"
            tabIndex={0}
            onClick={onAck}
            onKeyDown={(e) => { if (e.key === 'Enter') onAck(); }}
            sx={{
              fontWeight: 700,
              fontSize: '0.64rem',
              color: MUTED,
              cursor: 'pointer',
              opacity: 0,
              px: 0.75,
              border: `1px solid ${EDGE}`,
              borderRadius: 0.75,
              '&:hover': { color: 'text.primary' },
            }}
          >
            ack
          </Typography>
        )}
        <Typography variant="caption" sx={{ fontSize: '0.64rem', color: DIM, minWidth: 36, textAlign: 'right' }}>
          {ago(group.latestAt)}
        </Typography>
      </Box>
      {open && expandable && (
        <Box sx={{ pl: 3.2, pr: 1, pb: 1 }}>
          {group.occurrences.map((occ) => (
            <Typography
              key={occ.id}
              variant="caption"
              sx={{
                display: 'block',
                fontSize: '0.62rem',
                color: DIM,
                borderLeft: `1px solid ${EDGE}`,
                pl: 1.25,
                py: 0.15,
              }}
            >
              {ago(occ.at)}{occ.detail ? ` · ${occ.detail}` : ''}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};

const StandingCard: React.FC<{
  group: AlertGroup;
  routing?: boolean;
  onAck: () => void;
  onFocus?: () => void;
  onRouteAround?: () => void;
  onWatchlist?: () => void;
  onExcludeOwn?: () => void;
  watchlisting?: boolean;
  excluding?: boolean;
  report?: ReturnType<typeof buildThreatReport>;
  onPromotePacket?: () => void;
}> = ({
  group, routing, onAck, onFocus, onRouteAround, onWatchlist, onExcludeOwn,
  watchlisting, excluding, report, onPromotePacket,
}) => {
  const [open, setOpen] = useState(false);
  const shown = group.occurrences.slice(0, 12);

  const reveal = () => {
    const next = !open;
    setOpen(next);
    if (next) onFocus?.();
  };

  return (
    <Box
      sx={{
        mx: 0.25,
        mb: 1,
        pl: 0,
        pr: 1.4,
        py: 1.2,
        borderRadius: 1.25,
        border: `1px solid ${EDGE}`,
        bgcolor: group.tone === 'crit' ? 'rgba(239,83,80,0.05)' : 'rgba(255,255,255,0.02)',
        display: 'grid',
        gridTemplateColumns: '3px 1fr',
        gap: 1.25,
      }}
    >
      <Box sx={{ borderRadius: '0 2px 2px 0', bgcolor: TONE_RAIL[group.tone] }} />
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ position: 'relative', width: 10, height: 10, flexShrink: 0 }}>
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                border: `1.5px solid ${TONE_RAIL[group.tone]}`,
                '@keyframes ping': {
                  to: { transform: 'scale(2.6)', opacity: 0 },
                },
                animation: 'ping 2s ease-out infinite',
                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
              }}
            />
            <Box sx={{ position: 'absolute', inset: 0, borderRadius: '50%', bgcolor: TONE_RAIL[group.tone] }} />
          </Box>
          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.78rem', color: TONE_COLOR[group.tone] }}>
            {group.title}
          </Typography>
          {group.states.map((state) => (
            <Entity key={state}>{state}</Entity>
          ))}
          {group.entity && <Entity>{group.entity}</Entity>}
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 0.7 }}>
          {group.detail && (
            <Typography variant="caption" sx={{ fontSize: '0.64rem', color: MUTED }}>
              {group.detail.replace(/^ahead /, 'ahead ')}
            </Typography>
          )}
          <Typography variant="caption" sx={{ fontSize: '0.64rem', color: MUTED }}>
            first seen {formatWhen(group.firstAt)}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: '0.64rem', color: MUTED }}>
            last confirmed {formatWhen(group.latestAt)}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: '0.64rem', color: MUTED }}>
            confirmations ×{group.count}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
          {group.canRouteAround && onRouteAround && (
            <QuietBtn onClick={onRouteAround} disabled={routing}>
              {routing ? 'Routing…' : 'Route around'}
            </QuietBtn>
          )}
          {group.canExcludeOwn && (
            <QuietBtn onClick={onExcludeOwn} disabled={excluding}>
              {excluding ? 'Marking…' : 'Ours'}
            </QuietBtn>
          )}
          {group.promoteEventId && (
            <QuietBtn
              onClick={onWatchlist}
              disabled={group.alreadyWatchlisted || watchlisting}
            >
              {group.alreadyWatchlisted ? 'Watchlisted' : watchlisting ? 'Adding…' : 'Watchlist'}
            </QuietBtn>
          )}
          <QuietBtn onClick={reveal}>{open ? 'Hide' : 'Details'}</QuietBtn>
          <QuietBtn onClick={onAck}>Ack</QuietBtn>
        </Box>
        {open && (
          <Box sx={{ mt: 1, pt: 0.85, borderTop: `1px solid ${EDGE}` }}>
            {report ? (
              <MissionThreatReport
                report={report}
                onPromotePacket={onPromotePacket}
                onRouteOptions={onRouteAround}
                onMarkOurs={onExcludeOwn}
                promoting={watchlisting}
                routing={routing}
                marking={excluding}
              />
            ) : (
              <>
            {onFocus && (
              <Typography variant="caption" sx={{ display: 'block', fontSize: '0.62rem', color: MUTED, mb: 0.6 }}>
                Shown on the map
              </Typography>
            )}
            {shown.map((occ) => (
              <Typography
                key={occ.id}
                variant="caption"
                sx={{
                  display: 'block',
                  fontSize: '0.62rem',
                  color: DIM,
                  borderLeft: `1px solid ${EDGE}`,
                  pl: 1.25,
                  py: 0.15,
                }}
              >
                {formatWhen(occ.at)}{occ.detail ? ` · ${occ.detail}` : ''}
              </Typography>
            ))}
            {group.occurrences.length > shown.length && (
              <Typography variant="caption" sx={{ display: 'block', fontSize: '0.6rem', color: DIM, pl: 1.25, pt: 0.25 }}>
                +{group.occurrences.length - shown.length} earlier
              </Typography>
            )}
              </>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const QuietBtn: React.FC<{
  onClick?: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}> = ({ onClick, disabled, children }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    disabled={disabled}
    sx={{
      ...metalSx('gun'),
      height: 26,
      px: 1.25,
      display: 'inline-flex',
      alignItems: 'center',
      fontSize: '0.64rem',
      fontWeight: 600,
      cursor: disabled ? 'default' : 'pointer',
      fontFamily: 'inherit',
    }}
  >
    {children}
  </Box>
);

const MissionAlertWaterfall: React.FC<MissionAlertWaterfallProps> = ({
  mission,
  events,
  deviceLabel,
  units = 'mi',
  checking,
  emptyHint,
  onFocus,
  onAcked,
  onRouteAround,
  routing,
  timeline,
}) => {
  const [showHistory, setShowHistory] = useState(false);
  const [watchlisting, setWatchlisting] = useState<string | null>(null);
  const [excluding, setExcluding] = useState<string | null>(null);
  const [watchError, setWatchError] = useState<string | null>(null);
  const [packetTarget, setPacketTarget] = useState<PromoteTarget | null>(null);
  const feed = useMemo(
    () => buildAlertFeed(events, deviceLabel, { showHistory, units }),
    [events, deviceLabel, showHistory, units],
  );
  const drill = mission.current_run?.kind === 'rehearsal'
    || feed.standing.some((g) => g.drill)
    || feed.rows.some((g) => g.drill);

  const ack = async (ids?: number[]) => {
    if (!ids || ids.length === 0) {
      onAcked(await acknowledgeRouteEvents(mission.id));
      return;
    }
    let status: MissionRouteStatus | null = null;
    for (const id of ids) {
      status = await acknowledgeRouteEvents(mission.id, id);
    }
    if (status) onAcked(status);
  };

  const excludeOwn = async (group: AlertGroup) => {
    if (!group.excludeIdentifier) return;
    setExcluding(group.key);
    setWatchError(null);
    try {
      await trustIdentifier(mission.id, {
        identifier: group.excludeIdentifier,
        signal_type: group.excludeSignalType || 'wifi',
        name: group.excludeName,
        manufacturer_id: group.excludeManufacturerId ?? null,
      });
      onAcked(await getMissionRouteEvents(mission.id));
    } catch (e: any) {
      setWatchError(e?.response?.data?.detail || e?.message || 'Could not mark that as ours');
    } finally {
      setExcluding(null);
    }
  };

  const watchlist = async (group: AlertGroup) => {
    if (!group.promoteEventId || group.alreadyWatchlisted) return;
    setWatchlisting(group.key);
    setWatchError(null);
    try {
      await promoteRouteEvent(mission.id, group.promoteEventId, 'Promoted from the Live tab');
      onAcked(await getMissionRouteEvents(mission.id));
    } catch (e: any) {
      setWatchError(e?.response?.data?.detail || e?.message || 'Could not add that to the watchlist');
    } finally {
      setWatchlisting(null);
    }
  };

  const focusOf = (group: AlertGroup) => {
    if (!onFocus) return undefined;
    const lon = Number(group.longitude);
    const lat = Number(group.latitude);
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return undefined;
    return () => onFocus(lon, lat);
  };

  return (
    <Box>
      <DockStatStrip
        items={[
          { label: 'Open', value: feed.openCount, color: feed.openCount ? CORAL : MUTED, icon: <WarningAmberIcon /> },
          { label: 'Standing', value: feed.standing.length, color: GOLD, icon: <CampaignIcon /> },
          { label: 'Critical', value: feed.standing.filter((g) => g.tone === 'crit').length, color: CORAL, icon: <ShieldIcon /> },
          { label: 'Unacked', value: feed.standing.filter((g) => !g.acknowledged).length, color: AMBER, icon: <WarningAmberIcon /> },
          { label: 'History', value: feed.rows.length, color: '#42a5f5', icon: <HistoryIcon /> },
          { label: 'Events', value: events.length, color: MUTED },
          { label: 'Reroute', value: feed.standing.filter((g) => g.canRouteAround).length, color: '#00b8a9', icon: <AltRouteIcon /> },
          { label: 'Watchlist', value: feed.standing.filter((g) => g.alreadyWatchlisted || g.states.includes('watchlisted')).length, color: '#ce93d8', icon: <VisibilityIcon /> },
        ]}
      />
    <Box sx={{ mt: 0, border: `1px solid ${EDGE}`, borderRadius: 1.25, overflow: 'hidden', bgcolor: 'rgba(0,0,0,0.15)' }}>
      {drill && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 0.7,
            bgcolor: 'rgba(245,185,66,0.08)',
            borderBottom: '1px solid rgba(245,185,66,0.25)',
          }}
        >
          <Box
            sx={{
              px: 0.7,
              borderRadius: 0.4,
              bgcolor: GOLD,
              color: '#181203',
              fontSize: '0.58rem',
              fontWeight: 800,
              letterSpacing: 0.8,
            }}
          >
            DRILL
          </Box>
          <Typography variant="caption" sx={{ fontSize: '0.62rem', letterSpacing: 0.4, color: GOLD }}>
            Exercise — alerts are from a baseline run
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, pt: 1.25, pb: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          {showHistory
            ? 'All updates'
            : (
              <>
                <Box component="span" sx={{ color: feed.openCount > 0 ? CORAL : 'text.primary' }}>
                  {feed.openCount}
                </Box>
                {' '}open alert{feed.openCount === 1 ? '' : 's'}
              </>
            )}
        </Typography>
        <DockTabHelp tab="activity" />
        <Box sx={{ flex: 1 }} />
        {checking && (
          <Typography variant="caption" sx={{ fontSize: '0.6rem', color: DIM }}>checking…</Typography>
        )}
        {feed.openCount > 0 && !showHistory && (
          <Action onClick={() => ack()}>Ack all</Action>
        )}
        <Action onClick={() => setShowHistory((v) => !v)}>{showHistory ? 'Open only' : 'History'}</Action>
      </Box>
      {watchError && (
        <Typography variant="caption" sx={{ display: 'block', px: 1.5, pb: 0.75, color: CORAL, fontSize: '0.62rem' }}>
          {watchError}
        </Typography>
      )}

      {feed.standing.some((g) => g.canRouteAround) && onRouteAround && !showHistory && (
        <Box sx={{ px: 1.5, pb: 1 }}>
          <Box
            component="button"
            type="button"
            onClick={() => {
              const watch = feed.standing.some((g) => g.states.includes('watchlisted'));
              onRouteAround(
                watch ? 'avoid_watchlist' : 'route_around_threats',
                feed.standing[0],
              );
            }}
            disabled={routing}
            sx={{
              ...metalSx('steel'),
              height: 28,
              px: 1.4,
              display: 'inline-flex',
              alignItems: 'center',
              fontSize: '0.68rem',
              fontWeight: 700,
              cursor: routing ? 'default' : 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {routing ? 'Routing…' : 'Route around all threats'}
          </Box>
        </Box>
      )}

      {feed.standing.map((group) => {
        const report = trackedKind(group.eventType, group.threatKey)
          ? buildThreatReport({
            events,
            threatKey: group.threatKey,
            timeline,
            units,
          })
          : null;
        return (
        <StandingCard
          key={group.key}
          group={group}
          routing={routing}
          onAck={() => ack(group.eventIds)}
          onFocus={focusOf(group)}
          onRouteAround={onRouteAround
            ? () => onRouteAround(
              group.eventType === 'watchlist_on_route' || group.states.includes('watchlisted')
                ? 'avoid_watchlist'
                : 'route_around_threats',
              group,
            )
            : undefined}
          onWatchlist={() => watchlist(group)}
          onExcludeOwn={() => excludeOwn(group)}
          watchlisting={watchlisting === group.key}
          excluding={excluding === group.key}
          report={report}
          onPromotePacket={() => {
            if (!report) return;
            setPacketTarget({
              member_type: report.watchlistId ? 'watchlist' : 'device',
              member_id: String(report.watchlistId || report.identifier),
              label: report.title,
              meta: { threat_key: report.id, kind: report.kind },
            });
          }}
        />
        );
      })}
      <PromoteToPacketDialog
        open={!!packetTarget}
        onClose={() => setPacketTarget(null)}
        target={packetTarget}
        defaultName={packetTarget?.label}
      />

      {feed.standing.length === 0 && feed.rows.length === 0 ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1.5, pb: 1.25, fontSize: '0.65rem' }}>
          {emptyHint || 'Nothing to report.'}
        </Typography>
      ) : (
        <Box sx={{ borderTop: feed.standing.length ? `1px solid ${EDGE}` : 'none' }}>
          {feed.rows.map((group) => (
            <WaterfallRow
              key={group.key}
              group={group}
              onAck={() => ack(group.eventIds)}
              onFocus={focusOf(group)}
              onWatchlist={() => watchlist(group)}
              onExcludeOwn={() => excludeOwn(group)}
              watchlisting={watchlisting === group.key}
              excluding={excluding === group.key}
            />
          ))}
        </Box>
      )}
    </Box>
    </Box>
  );
};

export default MissionAlertWaterfall;
